// WbSettingsPage — /weighbridge/settings.
//
// Hub for every weighbridge settings page. Mirrors the design of the
// collections SettingsIndexPage — same shared Card grouping, same card
// row markup, same icon treatment and spacing.
//
// Cards are admin-only (StaffRole 'admin'). Non-admin staff see the
// same layout but the row is a non-interactive div with a Lock indicator
// instead of a Link.

import { Link } from 'react-router-dom';
import {
  Building2,
  Camera,
  ClipboardCheck,
  Cpu,
  FileText,
  Layers,
  Lock,
  Receipt,
  Scale,
  Shield,
  type LucideIcon,
} from 'lucide-react';
import { Card } from '../../../components/ui/Card/Card';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Tooltip } from '../../../components/ui/Tooltip/Tooltip';
import { useAuthStore } from '../../../store/authStore';
import styles from './WbSettingsPage.module.css';

interface SettingCard {
  to: string;
  label: string;
  description: string;
  icon: LucideIcon;
  /** When true, managers (not just admins) may open this card. */
  managerAllowed?: boolean;
}

interface SettingGroup {
  title: string;
  items: SettingCard[];
}

const GROUPS: SettingGroup[] = [
  {
    title: 'Hardware',
    items: [
      {
        to: '/weighbridge/settings/yards',
        label: 'Yards',
        description: 'Sites and locations',
        icon: Building2,
      },
      {
        to: '/weighbridge/settings/weighbridges',
        label: 'Weighbridges',
        description: 'Bridges, indicators, calibrations',
        icon: Scale,
      },
      {
        to: '/weighbridge/settings/cameras',
        label: 'Cameras',
        description: 'ANPR and snapshot cameras',
        icon: Camera,
      },
      {
        to: '/weighbridge/settings/barriers',
        label: 'Barriers & signals',
        description: 'Traffic arms and relay triggers',
        icon: Shield,
      },
      {
        to: '/weighbridge/settings/agents',
        label: 'Agents',
        description: 'On-premise agent pairing',
        icon: Cpu,
      },
    ],
  },
  {
    title: 'Catalogue',
    items: [
      {
        to: '/weighbridge/settings/product-groups',
        label: 'Product Groups',
        description: 'Organise products into groups for easier selection',
        icon: Layers,
        managerAllowed: true,
      },
    ],
  },
  {
    title: 'Financial',
    items: [
      {
        to: '/weighbridge/settings/vat',
        label: 'VAT',
        description: 'Registration, rates and defaults',
        icon: Receipt,
      },
      {
        to: '/weighbridge/settings/print-templates',
        label: 'Print templates',
        description: 'Tickets, advice notes, WTN',
        icon: FileText,
      },
    ],
  },
  {
    title: 'Compliance & operations',
    items: [
      {
        to: '/weighbridge/settings/compliance',
        label: 'Compliance & policies',
        description: 'Signatures, waste docs, tolerances',
        icon: ClipboardCheck,
      },
      {
        to: '/weighbridge/settings/locks',
        label: 'Lock Management',
        description: 'View and release active ticket locks',
        icon: Lock,
        managerAllowed: true,
      },
    ],
  },
];

export function WbSettingsPage(): JSX.Element {
  const role = useAuthStore((s) => s.user?.role ?? null);
  const isAdmin = role === 'admin';

  return (
    <div className={styles.page}>
      <PageHeader
        title="Settings"
        subtitle="Configure your weighbridge module"
      />

      {GROUPS.map((group) => (
        <Card key={group.title} title={group.title}>
          <ul className={styles.grid}>
            {group.items.map((item) =>
              isAdmin || (item.managerAllowed === true && role === 'manager') ? (
                <li key={item.to}>
                  <Link to={item.to} className={styles.card}>
                    <CardBody item={item} />
                  </Link>
                </li>
              ) : (
                <li key={item.to}>
                  <div
                    className={`${styles.card} ${styles.cardLocked}`}
                    aria-disabled="true"
                    data-testid={`wb-settings-card-locked-${item.label}`}
                  >
                    <CardBody item={item} />
                    <Tooltip content="Admin access required">
                      <span
                        className={styles.lock}
                        aria-label="Admin access required"
                      >
                        <Lock size={14} aria-hidden />
                      </span>
                    </Tooltip>
                  </div>
                </li>
              ),
            )}
          </ul>
        </Card>
      ))}
    </div>
  );
}

function CardBody({ item }: { item: SettingCard }): JSX.Element {
  const Icon = item.icon;
  return (
    <>
      <span className={styles.icon}>
        <Icon size={20} aria-hidden />
      </span>
      <span className={styles.text}>
        <span className={styles.label}>{item.label}</span>
        <span className={styles.description}>{item.description}</span>
      </span>
    </>
  );
}
